import React, { useState } from "react";

import { clsx } from "clsx";
import { useForm } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";

import {
  FormField,
  FormCheckbox,
  FormSelect,
  Callout,
  Button,
  IconAlert,
  type FormSelectProps,
} from "@bared/ui";

import { ky, hooks } from "~/lib/ky-utils";

import { useSessionCustomer } from "~/hooks/useSession";

import type { TestDrives } from "~/lib/test-drives";
import type { TestDrivesRequest, TestDrivesResponse } from "~/api/test-drives";

import { Sheet } from "./Sheet";

export function TestDrives() {
  const [isOpen, setIsOpen] = useState(false);

  const customer = useSessionCustomer();

  // Only show for employees.
  if (true !== customer?.isEmployee) return null;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={clsx(
          "flex gap-micro",
          !!customer.testDrives && "text-[#fcd34d]",
        )}
      >
        {!!customer.testDrives && <IconAlert className="w-mini" />}
        Test Drives
      </button>
      <Sheet
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Test Drives"
        layout="condensed"
        className="flex flex-col"
      >
        <TestDrivesForm testDrives={customer.testDrives} />
      </Sheet>
    </>
  );
}

interface FormType {
  saleMode: NonNullable<TestDrives["saleMode"]> | "";
  enableZendeskSandbox: NonNullable<TestDrives["enableZendeskSandbox"]>;
}

function TestDrivesForm({ testDrives }: { testDrives: TestDrives | null }) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { mutateAsync } = useMutation({
    mutationKey: ["test-drives"],
    mutationFn: async (data: TestDrivesRequest) =>
      ky
        .post("/api/test-drives", {
          hooks,
          json: data,
        })
        .json<TestDrivesResponse>(),
    onSuccess: () => {
      // Hard reload to use the new session token cookie.
      setIsRefreshing(true);
      window.location.reload();
    },
  });

  const form = useForm({
    defaultValues: {
      saleMode: testDrives?.saleMode ?? "",
      enableZendeskSandbox: testDrives?.enableZendeskSandbox ?? false,
    } as FormType,
    // Must return a promise.
    onSubmit: async ({ value }) => {
      await mutateAsync({
        testDrives: {
          ...(value.saleMode ? { saleMode: value.saleMode } : {}),
          ...(true === value.enableZendeskSandbox
            ? { enableZendeskSandbox: value.enableZendeskSandbox }
            : {}),
        },
      });
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className="flex flex-1 flex-col"
    >
      <div className="flex flex-1 flex-col gap-mini overflow-scroll p-mini">
        <Callout variant="secondary" size="large">
          Hello Bared employee. With test drives, you can override various
          settings which will only affect your account.
        </Callout>
        <form.Field
          name="saleMode"
          children={(field) => {
            return (
              <FormField name={field.name} label="Sale mode">
                <FormSelect
                  id={field.name}
                  name={field.name}
                  onBlur={field.handleBlur}
                  options={saleModeOptions}
                  value={field.state.value}
                  placeholder="Do not override"
                  onChange={(e) =>
                    field.handleChange(e.target.value as FormType["saleMode"])
                  }
                />
              </FormField>
            );
          }}
        />
        <form.Field
          name="enableZendeskSandbox"
          children={(field) => {
            return (
              <FormField name={field.name} label="Zendesk Sandbox">
                <FormCheckbox
                  id={field.name}
                  name={field.name}
                  onBlur={field.handleBlur}
                  checked={field.state.value}
                  label="Enable Zendesk Sandbox"
                  onChange={(e) => field.handleChange(e.target.checked)}
                />
              </FormField>
            );
          }}
        />
      </div>
      <div className="flex flex-col items-center gap-mini border-t border-primary p-mini pb-midi">
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button
              size="large"
              type="submit"
              className="w-full"
              disabled={isRefreshing || !canSubmit}
            >
              {isRefreshing
                ? "Refreshing, hold tight…"
                : isSubmitting
                  ? "Applying…"
                  : "Apply"}
            </Button>
          )}
        />
        <Button
          size="large"
          type="button"
          variant="link"
          onClick={() => form.reset(defaultValues)}
        >
          Reset test drives
        </Button>
      </div>
    </form>
  );
}

const saleModeOptions: FormSelectProps["options"] = [
  { value: "off", label: "No sale" },
  { value: "vip", label: "VIP sale" },
  { value: "general", label: "General sale" },
];

const defaultValues: FormType = {
  saleMode: "",
  enableZendeskSandbox: false,
};
